import { useLayoutEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { TextDivider } from 'src/v2/commonComponents/TextDivider';
import { Button } from 'src/v2/commonComponents/Button';
import {
  useCancelSubscriptionMutation,
  useUpdateCancelSubscriptionReasonMutation,
} from 'src/store/services';
import { useSession, useSubscriptionInfo } from 'src/hooks';
import {
  AppRoutes,
  CancellationReasonChoice,
  UserSettingsNestedRoutes,
} from 'src/types';
import { LoadingOverlay } from 'src/pages/PaymentBetaPage/components/LoadingOverlay';
import {
  CANCEL_SUBSCRIPTION_REASONS,
  PLACEHOLDER_BY_REASON,
} from 'src/constants';
import { CancelSubscriptionReasonItem } from '../CancelSubscriptionReasonItem';
import styles from './CancelSubscriptionReason.module.scss';
import { CancelSubscriptionSteps } from 'src/v2/pages/subscription/components/CancelSubscription';

type CancelSubscriptionReasonProps = {
  onChangeStep: (value: number) => void;
};

export const CancelSubscriptionReason = ({
  onChangeStep,
}: CancelSubscriptionReasonProps) => {
  const [selectedValue, setSelectedValue] = useState<
    CancellationReasonChoice | undefined
  >(undefined);
  const [reasonDescription, setReasonDescription] = useState<string>('');
  const [isLoadingForRedirect, setIsLoadingForRedirect] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const { appUser } = useSession();
  const {
    subscriptionInfo,
    isSubscriptionInfoFetching,
    isSubscriptionInfoLoading,
  } = useSubscriptionInfo();

  const [
    updateCancelSubscriptionReason,
    { isLoading: isUpdateCancelSubscriptionReasonLoading },
  ] = useUpdateCancelSubscriptionReasonMutation();

  const [cancelSubscription, { isLoading: isCancelSubscriptionLoading }] =
    useCancelSubscriptionMutation();

  const handleReasonChange = (value: CancellationReasonChoice) => {
    setSelectedValue(value);
    setReasonDescription('');
  };

  const handleChangeReasonDescription = (value: string) => {
    setReasonDescription(value);
  };

  const handleGoBackClick = () => {
    navigate(-1);
  };

  const handleUnsubscribe = async () => {
    if (!appUser.user_id) {
      return;
    }

    if (!selectedValue) {
      return;
    }

    try {
      setIsLoadingForRedirect(true);
      await updateCancelSubscriptionReason({
        user_id: appUser.user_id,
        reason: {
          choice: selectedValue,
          extra_text: reasonDescription,
        },
      }).unwrap();
      if (!subscriptionInfo?.is_cancelled) {
        await cancelSubscription(appUser.user_id).unwrap();
      }
      setIsLoadingForRedirect(false);
      onChangeStep(CancelSubscriptionSteps.CANCELLATION_DONE);
    } catch (error) {
      toast.error('Failed to cancel subscription.');
      setIsLoadingForRedirect(false);
    }
  };

  const isCancelSubscriptionDisabled =
    !selectedValue ||
    !reasonDescription ||
    isUpdateCancelSubscriptionReasonLoading ||
    isLoadingForRedirect ||
    isCancelSubscriptionLoading;

  useLayoutEffect(() => {
    if (!subscriptionInfo) {
      navigate(
        `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.SUBSCRIPTION}`,
      );
    }
  }, [subscriptionInfo, navigate]);

  const placeholder =
    PLACEHOLDER_BY_REASON[selectedValue as CancellationReasonChoice] ??
    'Tell us more';

  if (
    isSubscriptionInfoFetching ||
    isSubscriptionInfoLoading ||
    isUpdateCancelSubscriptionReasonLoading ||
    isCancelSubscriptionLoading ||
    isLoadingForRedirect
  ) {
    return <LoadingOverlay label="Loading..." />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2>Before you go!</h2>
        </div>

        <h5 className={styles.title}>
          Help us to improve Ninja AI, please share your reason for canceling
        </h5>

        <div className={styles.content}>
          <p className={styles.caption}>Required</p>
          {CANCEL_SUBSCRIPTION_REASONS.map(({ value, label }) => (
            <CancelSubscriptionReasonItem
              key={value}
              value={value}
              selectedValue={selectedValue}
              placeholder={placeholder}
              label={label}
              reasonDescription={reasonDescription}
              onReasonChange={handleReasonChange}
              onChangeReasonDescription={handleChangeReasonDescription}
            />
          ))}
        </div>
      </div>

      <TextDivider />

      <div className={styles.footer}>
        <Button onClick={handleGoBackClick}>Keep Subscription</Button>

        <Button
          color="secondary-outline"
          onClick={handleUnsubscribe}
          disabled={isCancelSubscriptionDisabled}
        >
          Continue cancellation
        </Button>
      </div>
    </div>
  );
};
